import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Package,
  Search,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PageHeaderToolbar } from '@/components/app/PageHeader'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarImage, AgentAvatarFallback } from '@/components/ui/avatar'
import { KpiCard, KpiCardSkeleton } from '@/features/dashboard/components/KpiCard'
import { programStatusBadgeClass } from '@/features/dashboard/utils/semanticBadges'
import { buildProspectDetailPath } from '@/features/prospects/paths'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAppLocale } from '@/lib/locale'

import { useAuthSession } from '../../auth/session'
import { fetchAgent, reactivateAgent, suspendAgent } from '../api'
import { AgentLifecycleConfirmDialog, type AgentLifecycleAction } from '../components/AgentActionDialogs'

import type { AgentProspectSummary, AgentRecord } from '@/types/agents'

/* ── Presentation helpers ── */

function getStageLabel(t: (key: string) => string, stage: string): string {
  const map: Record<string, string> = {
    suspect: t('agents.detail.stages.suspect'),
    prospect_froid: t('agents.detail.stages.prospect_froid'),
    prospect_tiede: t('agents.detail.stages.prospect_tiede'),
    prospect_chaud: t('agents.detail.stages.prospect_chaud'),
  }
  return map[stage] ?? stage
}

function getSubmissionLabel(t: (key: string) => string, status: string): string {
  const map: Record<string, string> = {
    pending_sync: t('agents.detail.syncStatuses.pending_sync'),
    synced: t('agents.detail.syncStatuses.synced'),
    sync_failed: t('agents.detail.syncStatuses.sync_failed'),
    deleted: t('agents.detail.syncStatuses.deleted'),
  }
  return map[status] ?? status
}

function stageBadgeClass(stage: string) {
  if (stage === 'prospect_chaud') {
    return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
  }
  if (stage === 'prospect_tiede') {
    return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
  }
  if (stage === 'prospect_froid') {
    return 'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300'
  }
  return 'border-transparent bg-muted text-muted-foreground'
}

function syncBadgeClass(status: string) {
  if (status === 'synced') {
    return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
  }
  if (status === 'pending_sync') {
    return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
  }
  if (status === 'sync_failed') {
    return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
  }
  return 'border-transparent bg-muted text-muted-foreground'
}

function conversionStatusLabel(status: string, t: (key: string) => string) {
  const map: Record<string, string> = {
    open: t('dashboard.agentRecentProspects.conversionStatuses.open'),
    converted: t('dashboard.agentRecentProspects.conversionStatuses.converted'),
    lost: t('dashboard.agentRecentProspects.conversionStatuses.lost'),
    locked: t('dashboard.agentRecentProspects.conversionStatuses.locked'),
  }
  return map[status] ?? status
}

function conversionStatusBadgeClass(status: string): string {
  switch (status) {
    case 'converted':
      return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
    case 'lost':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    case 'locked':
      return 'border-transparent bg-violet-500/15 text-violet-900 dark:bg-violet-500/20 dark:text-violet-300'
    default:
      return 'border-transparent bg-sky-500/15 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300'
  }
}

function agentStatusLabel(t: (key: string) => string, status: string): string {
  const map: Record<string, string> = {
    active: t('agents.status.active'),
    pending: t('agents.status.pending'),
    pending_activation: t('agents.status.pendingActivation'),
    suspended: t('agents.status.suspended'),
    invited: t('agents.status.invited'),
  }
  return map[status] ?? t('common.unknown')
}

function agentStatusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
    case 'suspended':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    case 'pending':
    case 'pending_activation':
      return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

/* ── Helpers ── */

function formatDate(value: string | null, withTime = false, fallback = '—', locale = getAppLocale()) {
  if (!value) {
    return fallback
  }
  return new Date(value).toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

type AgentProspectSortKey = 'prospect' | 'program' | 'stage' | 'submitted'

function sortProspects(
  left: AgentProspectSummary,
  right: AgentProspectSummary,
  key: AgentProspectSortKey,
  direction: SortDirection,
  t: (key: string) => string,
) {
  const modifier = direction === 'asc' ? 1 : -1
  const result =
    key === 'submitted'
      ? new Date(left.submitted_at ?? 0).getTime() - new Date(right.submitted_at ?? 0).getTime()
      : key === 'stage'
        ? getStageLabel(t, left.pipeline_stage).localeCompare(getStageLabel(t, right.pipeline_stage))
        : key === 'program'
          ? (left.program_name ?? '').localeCompare(right.program_name ?? '')
          : left.contact_name.localeCompare(right.contact_name)

  return result * modifier
}

/* ── Skeleton ── */

function AgentDetailSkeleton() {
  return (
    <section className="app-section">
      {/* Header */}
      <div className="flex flex-col gap-3 pb-1.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="size-12 rounded-full" />
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-40 rounded-md" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-28 rounded-md" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>

      {/* Programs Table */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-5 space-y-2">
          <Skeleton className="h-5 w-40 rounded-md" />
          <Skeleton className="h-4 w-56 rounded-md" />
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="h-11 bg-muted/30" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                index % 2 === 1 && 'bg-muted/15',
                index > 0 && 'border-t border-border/50',
              )}
            >
              <Skeleton className="h-4 w-40 rounded-md" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-4 w-24 rounded-md" />
              <Skeleton className="ml-auto h-4 w-16 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Prospects Table */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40 rounded-md" />
            <Skeleton className="h-4 w-56 rounded-md" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-44 rounded-md" />
            <Skeleton className="h-8 w-32 rounded-md" />
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="h-11 bg-muted/30" />
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                index % 2 === 1 && 'bg-muted/15',
                index > 0 && 'border-t border-border/50',
              )}
            >
              <Skeleton className="h-4 w-32 rounded-md" />
              <Skeleton className="h-4 w-28 rounded-md" />
              <Skeleton className="h-4 w-14 rounded-md" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="ml-auto h-4 w-24 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Main Page ── */

export function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>()
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuthSession()
  const canSuspend = hasPermission('agent.suspend')
  const canReactivate = hasPermission('agent.reactivate')

  const [pendingLifecycleAction, setPendingLifecycleAction] = useState<{
    type: AgentLifecycleAction
    agent: AgentRecord
  } | null>(null)
  const [search, setSearch] = useState('')
  const [stageFilter, setStageFilter] = useState('all')
  const [syncFilter, setSyncFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState<AgentProspectSortKey>('submitted')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const query = useQuery({
    queryKey: ['agents', 'detail', agentId],
    queryFn: () => fetchAgent(agentId ?? ''),
    enabled: Boolean(agentId),
  })

  const suspendMutation = useMutation({
    mutationFn: ({ agentId, reason }: { agentId: string; reason: string }) =>
      suspendAgent(agentId, { reason }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
        queryClient.invalidateQueries({ queryKey: ['agents', 'detail', agentId] }),
      ])
      setPendingLifecycleAction(null)
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: ({ agentId }: { agentId: string }) => reactivateAgent(agentId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
        queryClient.invalidateQueries({ queryKey: ['agents', 'detail', agentId] }),
      ])
      setPendingLifecycleAction(null)
    },
  })

  useEffect(() => {
    setPage(1)
  }, [search, stageFilter, syncFilter])

  const agent = query.data?.data
  const assignedPrograms = agent?.assigned_programs ?? []
  const prospects = agent?.prospects ?? []

  useAppBreadcrumbTrail(
    agent
      ? [
          { label: t('navigation.agents'), to: '/agents' },
          { label: agent.display_name ?? t('agents.detail.breadcrumbFallback') },
        ]
      : null,
  )

  const actionsBusy = suspendMutation.isPending || reactivateMutation.isPending

  const filteredProspects = useMemo(() => {
    const q = search.trim().toLowerCase()
    return prospects.filter((prospect) => {
      if (stageFilter !== 'all' && prospect.pipeline_stage !== stageFilter) return false
      if (syncFilter !== 'all' && prospect.submission_status !== syncFilter) return false
      if (!q) return true
      return [
        prospect.contact_name,
        prospect.company_name ?? '',
        prospect.program_name ?? '',
      ].some((value) => value.toLowerCase().includes(q))
    })
  }, [prospects, search, stageFilter, syncFilter])

  const sortedProspects = useMemo(
    () => [...filteredProspects].sort((left, right) => sortProspects(left, right, sortKey, sortDirection, t)),
    [filteredProspects, sortDirection, sortKey, t],
  )

  const totalFiltered = sortedProspects.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sortedProspects.slice(start, start + pageSize)
  }, [pageSize, safePage, sortedProspects])

  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  function handleSort(nextKey: AgentProspectSortKey) {
    setPage(1)
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(nextKey)
    setSortDirection(nextKey === 'submitted' ? 'desc' : 'asc')
  }

  if (!agentId) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('agents.detail.error.missingId')}
      </article>
    )
  }

  if (query.isPending) {
    return <AgentDetailSkeleton />
  }

  if (query.isError || !query.data) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(query.error as ApiError).message}
      </article>
    )
  }

  if (!agent) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('agents.detail.error.notFound')}
      </article>
    )
  }

  const statusLabelText = agentStatusLabel(t, agent.status)
  const title = agent.display_name ?? t('agents.detail.unknownName')
  const programsCount = agent.assigned_programs_count ?? assignedPrograms.length
  const prospectsCount = agent.total_prospects_count ?? prospects.length
  const convertedCount = agent.converted_prospects_count ?? 0
  const totalPoints = agent.total_points_earned ?? 0

  return (
    <section className="app-section">
      <AgentLifecycleConfirmDialog
        action={pendingLifecycleAction}
        isSubmitting={suspendMutation.isPending || reactivateMutation.isPending}
        error={
          suspendMutation.isError
            ? (suspendMutation.error as ApiError)
            : reactivateMutation.isError
              ? (reactivateMutation.error as ApiError)
              : null
        }
        onClose={() => {
          setPendingLifecycleAction(null)
          suspendMutation.reset()
          reactivateMutation.reset()
        }}
        onConfirm={async (action, selectedAgent, payload) => {
          if (action === 'suspend') {
            await suspendMutation.mutateAsync({
              agentId: selectedAgent.id,
              reason: payload.reason ?? '',
            })
            return
          }
          await reactivateMutation.mutateAsync({ agentId: selectedAgent.id })
        }}
      />

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon-sm" className="shrink-0">
            <Link to="/agents" aria-label={t('agents.detail.back')}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <Avatar className="size-12">
            {agent.avatar_url ? (
              <AvatarImage src={agent.avatar_url} alt={title} />
            ) : null}
            <AgentAvatarFallback seed={agent.id} className="text-sm">
              {title.slice(0, 2).toUpperCase()}
            </AgentAvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
              <Badge className={agentStatusBadgeClass(agent.status)}>{statusLabelText}</Badge>
            </div>
            {agent.agent_code ? (
              <p className="text-sm text-muted-foreground">
                {t('agents.detail.header.agentCode', { code: agent.agent_code })}
              </p>
            ) : null}
          </div>
        </div>
        <PageHeaderToolbar>
          {agent.actions?.can_suspend && canSuspend ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={actionsBusy}
              onClick={() => setPendingLifecycleAction({ type: 'suspend', agent })}
            >
              <UserX className="mr-2 h-4 w-4" />
              {t('agents.table.actions.suspend')}
            </Button>
          ) : null}
          {agent.actions?.can_reactivate && canReactivate ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={actionsBusy}
              onClick={() => setPendingLifecycleAction({ type: 'reactivate', agent })}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              {t('agents.table.actions.reactivate')}
            </Button>
          ) : null}
        </PageHeaderToolbar>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title={t('agents.detail.kpi.totalProspects.title')}
          value={prospectsCount.toLocaleString(locale)}
          description={t('agents.detail.kpi.totalProspects.description')}
          icon={Users}
          tone="primary"
        />
        <KpiCard
          title={t('agents.detail.kpi.converted.title')}
          value={convertedCount.toLocaleString(locale)}
          description={t('agents.detail.kpi.converted.description')}
          icon={CheckCircle2}
          tone="success"
        />
        <KpiCard
          title={t('agents.detail.kpi.points.title')}
          value={totalPoints.toLocaleString(locale)}
          description={t('agents.detail.kpi.points.description')}
          icon={Award}
          tone="warning"
        />
        <KpiCard
          title={t('agents.detail.kpi.programs.title')}
          value={programsCount.toLocaleString(locale)}
          description={t('agents.detail.kpi.programs.description')}
          icon={Package}
          tone="info"
        />
      </div>

      {/* ── Programs ── */}
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('agents.detail.programs.title')}</CardTitle>
              <CardDescription>{t('agents.detail.programs.description')}</CardDescription>
            </div>
            <Badge variant="secondary">
              {programsCount.toLocaleString(locale)} {programsCount === 1 ? t('agents.table.program') : t('agents.table.programs_plural')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {assignedPrograms.length === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('agents.detail.programs.noPrograms')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="text-xs font-semibold">{t('agents.detail.programsTable.program')}</TableHead>
                      <TableHead className="text-xs font-semibold">{t('agents.detail.programsTable.status')}</TableHead>
                      <TableHead className="text-xs font-semibold">{t('agents.detail.programsTable.assigned')}</TableHead>
                      <TableHead className="text-right text-xs font-semibold">{t('agents.detail.programsTable.points')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignedPrograms.map((assignment) => (
                      <TableRow
                        key={assignment.assignment_id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/programs/${assignment.program.id}`)}
                      >
                        <TableCell>
                          <p className="truncate text-sm font-semibold text-foreground">
                            {assignment.program.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {(assignment.program.assigned_agents_count ?? 0).toLocaleString(locale)} {t('agents.table.programs')}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(programStatusBadgeClass(assignment.program.status), 'capitalize')}>
                            {t(`programs.status.${assignment.program.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(assignment.assigned_at, false, t('agents.detail.dateUnavailable'))}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums">
                          {(assignment.points_earned ?? 0).toLocaleString(locale)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Prospects ── */}
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('agents.detail.prospects.title')}</CardTitle>
              <CardDescription>{t('agents.detail.prospects.description')}</CardDescription>
            </div>
            <Badge variant="secondary">{totalFiltered.toLocaleString(locale)} {totalFiltered === 1 ? t('agents.detail.prospects.prospect') : t('agents.detail.prospects.prospects_plural')}</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="mb-4 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('agents.detail.prospects.searchPlaceholder')}
                className="pl-9"
              />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="min-w-[150px]">
                <SelectValue placeholder={t('agents.detail.prospects.allStages')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('agents.detail.prospects.allStages')}</SelectItem>
                <SelectItem value="suspect">{getStageLabel(t, 'suspect')}</SelectItem>
                <SelectItem value="prospect_froid">{getStageLabel(t, 'prospect_froid')}</SelectItem>
                <SelectItem value="prospect_tiede">{getStageLabel(t, 'prospect_tiede')}</SelectItem>
                <SelectItem value="prospect_chaud">{getStageLabel(t, 'prospect_chaud')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={syncFilter} onValueChange={setSyncFilter}>
              <SelectTrigger className="min-w-[150px]">
                <SelectValue placeholder={t('agents.detail.prospects.allSyncs')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('agents.detail.prospects.allSyncs')}</SelectItem>
                <SelectItem value="pending_sync">{getSubmissionLabel(t, 'pending_sync')}</SelectItem>
                <SelectItem value="synced">{getSubmissionLabel(t, 'synced')}</SelectItem>
                <SelectItem value="sync_failed">{getSubmissionLabel(t, 'sync_failed')}</SelectItem>
                <SelectItem value="deleted">{getSubmissionLabel(t, 'deleted')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {totalFiltered === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('agents.detail.prospects.noResults')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <SortableTableHead
                        sortKey="prospect"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="text-xs font-semibold"
                      >
                        {t('agents.detail.table.prospect')}
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="program"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="text-xs font-semibold"
                      >
                        {t('agents.detail.table.program')}
                      </SortableTableHead>
                      <TableHead className="text-right text-xs font-semibold">{t('agents.detail.table.points')}</TableHead>
                      <SortableTableHead
                        sortKey="stage"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="text-xs font-semibold"
                      >
                        {t('agents.detail.table.stage')}
                      </SortableTableHead>
                      <TableHead className="text-xs font-semibold">{t('agents.detail.table.conversion')}</TableHead>
                      <TableHead className="text-xs font-semibold">{t('agents.detail.table.sync')}</TableHead>
                      <SortableTableHead
                        sortKey="submitted"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="text-xs font-semibold"
                      >
                        {t('agents.detail.table.submitted')}
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageSlice.map((prospect) => (
                      <TableRow key={prospect.id}>
                        <TableCell>
                          <Link
                            to={buildProspectDetailPath({ prospectId: prospect.id })}
                            className="group -m-1 block cursor-pointer rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                              {prospect.contact_name}
                            </p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {prospect.company_name ?? t('agents.detail.prospects.noCompany')}
                            </p>
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {prospect.program_name ?? t('agents.detail.prospects.noProgram')}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums">
                          {(prospect.points_awarded ?? 0).toLocaleString(locale)}
                        </TableCell>
                        <TableCell>
                          <Badge className={stageBadgeClass(prospect.pipeline_stage)}>
                            {getStageLabel(t, prospect.pipeline_stage)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={conversionStatusBadgeClass(prospect.conversion_status)}>
                            {conversionStatusLabel(prospect.conversion_status, t)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={syncBadgeClass(prospect.submission_status)}>
                            {getSubmissionLabel(t, prospect.submission_status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(prospect.submitted_at, true, t('agents.detail.dateUnavailable'))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <TablePaginationBar
                page={safePage}
                pageSize={pageSize}
                totalItems={totalFiltered}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 25, 50]}
              />
            </div>
          )}
        </CardContent>
      </Card>

    </section>
  )
}
